import type { MouseEvent } from "react";
import { Box, Typography, useTheme } from "@mui/material";
import { useTranslation } from "react-i18next";
import { ProductLineFormTreeView } from "./ProductLineFormTreeView";
import { useGetAllProductLines } from "src/api/client-api/productLines/useProductLines";
import { buildTreeStructure } from "./buildTreeStructure";
import { ProductFormProductLine } from "src/pages/Product/ProductForm/ProductForm.types";
import { SearchBox } from "@foodpilot/foods";

interface ProductLineFormTreeSelectorProps {
    selectedProductLine: ProductFormProductLine | undefined;
    onSelectionChange: (productLineName: string | undefined) => void;
    noSelectedItemPlaceholder: string;
}

export const ProductLineFormTreeSelector = ({
    selectedProductLine,
    onSelectionChange,
    noSelectedItemPlaceholder,
}: ProductLineFormTreeSelectorProps) => {
    const { t } = useTranslation();
    const theme = useTheme();
    const { productLines = [], searchApi } = useGetAllProductLines();

    const treeData = (() => {
        return buildTreeStructure(productLines);
    })();

    const handleSelectionChange = (itemId: string | null) => {
        // itemId will be the product line name or null
        onSelectionChange(itemId || undefined);
    };

    const handleContainerClick = (event: MouseEvent) => {
        // Prevent clicks within the TreeView from bubbling up to the popover
        event.stopPropagation();
    };

    return (
        <Box
            onClick={handleContainerClick}
            sx={{
                width: "350px",
                maxHeight: "400px",
                overflowY: "auto",
                padding: theme.spacing(2),
                border: `1px solid ${theme.custom.grey[500]}`,
                borderRadius: "8px",
                backgroundColor: "#FFF",
            }}
        >
            <Box borderBottom={`1px solid ${theme.custom.grey[500]}`} paddingBottom={2}>
                <SearchBox
                    value={searchApi.search}
                    onChange={(value: string) => {
                        searchApi.setSearch(value);
                    }}
                />
            </Box>

            {treeData.length === 0 ?
                <Typography variant="body" color="text.secondary">
                    {t("productLine.select.noOptions", "No product lines available")}
                </Typography>
            :   <ProductLineFormTreeView
                    items={treeData}
                    onSelectedItem={handleSelectionChange}
                    selectedItem={selectedProductLine || null}
                />
            }
        </Box>
    );
};
